import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/components/ui/use-toast";
import { ChevronRight, ChevronDown, Package, Loader2, Maximize2, Minimize2, ZoomIn, ZoomOut, RotateCcw, Search, X } from "lucide-react";
import { getOnSaleProducts, OnSaleProduct, FORECAST_SEARCH_IDS } from "@/services/api/forecast";
import { cn } from "@/lib/utils";
import * as echarts from 'echarts';

// 脑图节点接口
interface MindMapNode {
  id: string;
  name: string;
  type: 'brand' | 'spec' | 'mainSpu' | 'spu' | 'product';
  children?: MindMapNode[];
  salesCount?: number;
  data?: OnSaleProduct;
}

// 品牌排序顺序
const BRAND_ORDER = [
  'KIMTRUE且初',
  'ddg',
  'ONCUR安修泽',
  'Aucuz奥札',
  'EVERBAB艾蓓拉',
  '3odice三蒂丝'
];

// ECharts树形节点接口
interface EChartTreeNode {
  name: string;
  value?: number;
  children?: EChartTreeNode[];
  itemStyle?: {
    color?: string;
  };
  label?: {
    show?: boolean;
    position?: string;
    fontSize?: number;
    fontWeight?: string;
  };
  lineStyle?: {
    color?: string;
    width?: number;
  };
  cursor?: string;
  productCode?: string;
  productName?: string;
  collapsed?: boolean;
  nodeType?: 'brand' | 'spec' | 'mainSpu' | 'spu' | 'product';
  mainSpuName?: string;
  specName?: string;
  brandName?: string;
}

// 品牌选项接口
interface BrandOption {
  code: string;
  name: string;
}

// 品牌数据源
const BRAND_OPTIONS: BrandOption[] = [
  { code: 'B2214', name: 'KIMTRUE且初' },
  { code: 'B00206', name: 'EVERBAB艾蓓拉' },
  { code: 'B02330', name: 'ddg' },
  { code: 'B02333', name: '3odice三蒂丝' },
  { code: 'B02335', name: 'ONCUR安修泽' },
  { code: 'B02407', name: 'Aucuz奥札' },
];

const OnSaleProducts: React.FC = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [mindMapData, setMindMapData] = useState<MindMapNode[]>([]);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [chartHeight, setChartHeight] = useState<number>(800); // 动态高度
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstance = useRef<echarts.ECharts | null>(null);

  // 品牌选择相关状态
  const [selectedBrandCode, setSelectedBrandCode] = useState<string>('B2214'); // 默认选择B2214

  // 旭日图相关状态
  const [showSunburst, setShowSunburst] = useState(false);
  const [selectedMainSpu, setSelectedMainSpu] = useState<MindMapNode | null>(null);
  const [sunburstData, setSunburstData] = useState<EChartTreeNode | null>(null);
  const sunburstChartRef = useRef<HTMLDivElement>(null);
  const sunburstChartInstance = useRef<echarts.ECharts | null>(null);

  // 获取在售商品数据
  const fetchOnSaleProducts = async () => {
    setLoading(true);
    try {
      const response = await getOnSaleProducts({
        search_id: FORECAST_SEARCH_IDS.ON_SALE_PRODUCTS,
        page_size: 1000,
        品牌编码: selectedBrandCode // 使用当前选择的品牌编码
      });

      console.log('API响应:', response); // 调试日志

      if ((response.res_status_code === '200' || response.res_status_code === '0') && response.res_content) {
        // 尝试从不同位置获取数据
        let productsData: OnSaleProduct[] = [];

        if (Array.isArray(response.res_content)) {
          productsData = response.res_content;
        } else if (response.res_content.data && Array.isArray(response.res_content.data)) {
          productsData = response.res_content.data;
        } else if (response.res_content.list && Array.isArray(response.res_content.list)) {
          productsData = response.res_content.list;
        } else if (response.res_content.data_list && Array.isArray(response.res_content.data_list)) {
          productsData = response.res_content.data_list;
        }

        console.log('解析到的商品数据:', productsData); // 调试日志

        if (productsData.length > 0) {
          // 构建脑图数据结构
          const mindMapData = buildMindMapData(productsData);
          setMindMapData(mindMapData);

          // 计算并设置动态高度
          const optimalHeight = calculateOptimalHeight(mindMapData);
          setChartHeight(optimalHeight);
        } else {
          console.warn('API返回成功但没有数据');
          setMindMapData([]);
          setChartHeight(400); // 重置为默认高度
        }
      } else {
        console.error('API返回非成功状态:', response);
        setMindMapData([]);
        setChartHeight(400); // 重置为默认高度
      }
    } catch (error) {
      console.error('获取在售商品数据失败:', error);
      setMindMapData([]);
      setChartHeight(400); // 重置为默认高度
    } finally {
      setLoading(false);
    }
  };

  
  // 构建脑图数据结构
  const buildMindMapData = (products: OnSaleProduct[]): MindMapNode[] => {
    const brandMap = new Map<string, MindMapNode>();

    products.forEach(product => {
      const { 品牌, 规格, 主SPU名称, SPU名称, 产品名称, 销售数量 } = product;

      // 品牌层级
      if (!brandMap.has(品牌)) {
        brandMap.set(品牌, {
          id: `brand_${品牌}`,
          name: 品牌,
          type: 'brand',
          children: [],
          salesCount: 0
        });
      }

      const brandNode = brandMap.get(品牌)!;
      brandNode.salesCount = (brandNode.salesCount || 0) + 销售数量;

      // 规格层级
      let specNode = brandNode.children?.find(child => child.name === 规格);
      if (!specNode) {
        specNode = {
          id: `spec_${品牌}_${规格}`,
          name: 规格,
          type: 'spec',
          children: [],
          salesCount: 0
        };
        brandNode.children?.push(specNode);
      }
      specNode.salesCount = (specNode.salesCount || 0) + 销售数量;

      // 主SPU层级
      let mainSpuNode = specNode.children?.find(child => child.name === 主SPU名称);
      if (!mainSpuNode) {
        mainSpuNode = {
          id: `main_spu_${品牌}_${规格}_${主SPU名称}`,
          name: 主SPU名称,
          type: 'mainSpu',
          children: [],
          salesCount: 0
        };
        specNode.children?.push(mainSpuNode);
      }
      mainSpuNode.salesCount = (mainSpuNode.salesCount || 0) + 销售数量;

      // SPU层级
      let spuNode = mainSpuNode.children?.find(child => child.name === SPU名称);
      if (!spuNode) {
        spuNode = {
          id: `spu_${品牌}_${规格}_${主SPU名称}_${SPU名称}`,
          name: SPU名称,
          type: 'spu',
          children: [],
          salesCount: 0
        };
        mainSpuNode.children?.push(spuNode);
      }
      spuNode.salesCount = (spuNode.salesCount || 0) + 销售数量;

      // 产品层级
      const productNode: MindMapNode = {
        id: `product_${产品名称}`,
        name: 产品名称,
        type: 'product',
        salesCount: 销售数量,
        data: product
      };
      spuNode.children?.push(productNode);
    });

    // 按销售数量倒序排序第一层节点（品牌）
    const sortedBrands = Array.from(brandMap.values()).sort((a, b) => {
      // 按销售数量倒序排列
      const salesDiff = (b.salesCount || 0) - (a.salesCount || 0);
      if (salesDiff !== 0) return salesDiff;

      // 如果销售数量相同，按品牌名称排序
      return a.name.localeCompare(b.name);
    });

    // 对每个品牌下的规格节点按销售数量倒序排序
    sortedBrands.forEach(brand => {
      if (brand.children && brand.children.length > 0) {
        brand.children.sort((a, b) => {
          // 按销售数量倒序排列
          const salesDiff = (b.salesCount || 0) - (a.salesCount || 0);
          if (salesDiff !== 0) return salesDiff;

          // 如果销售数量相同，按规格名称排序
          return a.name.localeCompare(b.name);
        });
      }
    });

    return sortedBrands;
  };

  // 将脑图数据转换为ECharts树形数据格式
  const convertToEChartData = (mindMapData: MindMapNode[]): EChartTreeNode[] => {
    return mindMapData.map(brand => convertNodeToEChartNode(brand));
  };

  // 转换单个节点
  const convertNodeToEChartNode = (node: MindMapNode, parentPath: { brand?: string; spec?: string; mainSpu?: string } = {}): EChartTreeNode => {
    const displayName = node.salesCount && node.salesCount > 0
      ? `${node.name} (${node.salesCount.toLocaleString()})`
      : node.name;

    const echartNode: EChartTreeNode = {
      name: displayName,
      value: node.salesCount || 0,
      nodeType: node.type,
    };

    // 设置层级路径信息
    if (node.type === 'brand') {
      echartNode.brandName = node.name;
      parentPath.brand = node.name;
    } else if (node.type === 'spec') {
      echartNode.specName = node.name;
      echartNode.brandName = parentPath.brand;
      parentPath.spec = node.name;
    } else if (node.type === 'mainSpu') {
      echartNode.mainSpuName = node.name;
      echartNode.specName = parentPath.spec;
      echartNode.brandName = parentPath.brand;
      parentPath.mainSpu = node.name;
    }

    // 根据节点类型设置样式
    switch (node.type) {
      case 'brand':
        echartNode.itemStyle = { color: '#3B82F6' }; // 蓝色
        echartNode.label = {
          show: true,
          position: 'right',
          fontSize: 16,
          fontWeight: 'bold'
        };
        echartNode.lineStyle = { color: '#3B82F6', width: 3 };
        echartNode.collapsed = false; // 品牌节点默认展开
        break;
      case 'spec':
        echartNode.itemStyle = { color: '#10B981' }; // 绿色
        echartNode.label = {
          show: true,
          position: 'right',
          fontSize: 14,
          fontWeight: '600'
        };
        echartNode.lineStyle = { color: '#10B981', width: 2 };
        echartNode.collapsed = false; // 规格节点默认展开
        break;
      case 'mainSpu':
        echartNode.itemStyle = { color: '#F59E0B' }; // 橙色
        echartNode.label = {
          show: true,
          position: 'right',
          fontSize: 13,
          fontWeight: '600'
        };
        echartNode.lineStyle = { color: '#F59E0B', width: 2 };
        echartNode.collapsed = false; // 主SPU节点默认展开
        echartNode.cursor = 'pointer'; // 主SPU节点可点击
        break;
      case 'spu':
        echartNode.itemStyle = { color: '#8B5CF6' }; // 紫色
        echartNode.label = {
          show: true,
          position: 'right',
          fontSize: 12,
          fontWeight: '500'
        };
        echartNode.lineStyle = { color: '#8B5CF6', width: 1 };
        break;
      case 'product':
        echartNode.itemStyle = { color: '#6B7280' }; // 灰色
        echartNode.label = {
          show: true,
          position: 'right',
          fontSize: 11,
          fontWeight: 'normal'
        };
        echartNode.lineStyle = { color: '#6B7280', width: 1 };
        // 为产品节点添加额外标识和数据
        echartNode.cursor = 'pointer'; // 鼠标悬停时显示手型
        if (node.data) {
          echartNode.productCode = node.data.dim_goods_no;
          echartNode.productName = node.data.产品名称;
          console.log('产品节点数据:', {
            name: node.name,
            productCode: echartNode.productCode,
            productName: echartNode.productName
          }); // 调试信息
        }
        break;
    }

    // 递归处理子节点
    if (node.children && node.children.length > 0) {
      echartNode.children = node.children.map(child => convertNodeToEChartNode(child, { ...parentPath }));
    }

    return echartNode;
  };

  // 计算第三层节点数量并返回合适的高度
  const calculateOptimalHeight = (mindMapData: MindMapNode[]): number => {
    let thirdLevelNodeCount = 0;

    // 遍历品牌 → 规格 → 主SPU（第三层）
    mindMapData.forEach(brand => {
      brand.children?.forEach(spec => {
        if (spec.children) {
          thirdLevelNodeCount += spec.children.length;
        }
      });
    });

    // 按照20个节点占满一屏的比例计算高度
    const availableHeight = isFullscreen ? window.innerHeight - 120 : 800; // 可用高度
    const nodesPerScreen = 28; // 一屏显示20个节点
    const heightPerNode = availableHeight / nodesPerScreen; // 每个节点需要的高度

    // 为左侧品牌树结构预留额外空间
    const reservedLeftSpace = 200; // 左侧预留空间
    const calculatedHeight = reservedLeftSpace + (thirdLevelNodeCount * heightPerNode);

    // 设置最小和最大高度限制
    const minHeight = isFullscreen ? window.innerHeight - 120 : 800;
    const maxHeight = isFullscreen ? window.innerHeight - 120 : 8000;

    // 返回合适的高度值
    const optimalHeight = Math.max(minHeight, Math.min(calculatedHeight, maxHeight));

    console.log(`第三层节点数量: ${thirdLevelNodeCount}, 每节点高度: ${heightPerNode.toFixed(2)}px, 计算高度: ${calculatedHeight}, 最终高度: ${optimalHeight}`);

    return optimalHeight;
  };

  // 初始化和更新ECharts图表
  const updateChart = () => {
    if (!chartRef.current || mindMapData.length === 0) return;

    // 如果图表实例已存在，先销毁
    if (chartInstance.current) {
      chartInstance.current.dispose();
    }

    // 获取容器尺寸
    const container = chartRef.current;
    const width = container.offsetWidth;
    const height = container.offsetHeight;

    // 创建新的图表实例，指定确切尺寸
    chartInstance.current = echarts.init(chartRef.current, null, {
      width: width,
      height: height
    });

    const echartData = convertToEChartData(mindMapData);

    console.log('转换后的ECharts数据:', echartData); // 调试信息
    console.log('品牌数据长度:', mindMapData.length); // 检查品牌数量
    if (mindMapData.length > 0) {
      console.log('第一个品牌的结构:', mindMapData[0]); // 检查第一个品牌的子节点
    }

    const option = {
      tooltip: {
        trigger: 'item',
        triggerOn: 'mousemove',
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderColor: '#ccc',
        borderWidth: 1,
        textStyle: {
          color: '#333'
        },
        formatter: function(params: any) {
          const fullName = params.name;
          const value = params.value || 0;
          const level = params.treePathInfo ? params.treePathInfo.length - 1 : 1;
          const levelNames = ['品牌', '规格', '主SPU', 'SPU', '产品'];
          const levelName = levelNames[level] || '未知';

          // 从名称中提取纯名称（去掉括号和数量）
          const pureName = fullName.replace(/\s*\(\d[\d,]*\)$/, '');

          // 根据节点类型添加不同的点击提示
          let clickHint = '';
          if (level === 4) {
            clickHint = '<div style="color: #10B981; font-size: 11px; margin-top: 6px; font-style: italic;">👆 点击查看库存预测</div>';
          } else if (level === 2) {
            clickHint = '<div style="color: #F59E0B; font-size: 11px; margin-top: 6px; font-style: italic;">👆 点击查看旭日图</div>';
          }

          return `<div style="padding: 8px;">
            <div style="font-weight: bold; margin-bottom: 4px;">${pureName}</div>
            <div style="color: #666; font-size: 12px;">${levelName}</div>
            <div style="color: #3B82F6; margin-top: 4px;">销量: ${value.toLocaleString()}</div>
            ${clickHint}
          </div>`;
        }
      },
      series: [
        {
          type: 'tree',
          data: echartData,
          layout: 'orthogonal', // 使用正交布局
          orient: 'LR', // 从左到右
          roam: true, // 启用缩放和平移
          zoom: 1,
          top: '0%',
          left: '1%',
          bottom: '2%',
          right: '25%',
          initialTreeDepth: 3, // 初始展开三层（品牌→规格→主SPU）
          // 强制展开前3层
          collapseInterval: 0,
          expandAndCollapse: true,
          symbolSize: 20, // 减小节点大小
          // 调整节点间距，让连线更短
          nodePadding: 20, // 固定间距，让连线更短
          label: {
            show: true,
            position: 'right',
            fontSize: 14,
            color: '#000'
          },
          emphasis: {
            focus: 'descendant',
            scale: true,
            scaleSize: 20, // 鼠标悬停时放大节点
            label: {
              show: true,
              fontSize: 14,
              fontWeight: 'bold'
            }
          },
          blur: {
            itemStyle: {
              opacity: 0.4
            },
            label: {
              opacity: 0.4
            },
            lineStyle: {
              opacity: 0.4
            }
          },
          expandAndCollapse: true,
          initialTreeDepth: 1, // 初始只展开第一层，减少密集度
          animationDuration: 550,
          animationDurationUpdate: 750,
          lineStyle: {
            color: '#999',
            width: 2,
            curveness: 0.3, // 减小曲线度，让连线更直更短
            type: 'curve' // 使用曲线连接
          },
          // 设置节点间距和层级距离
          nodeScaleRatio: 1, // 恢复正常节点比例
          // 减少层级间的距离
          levelSeparation: 30, // 层级间的水平距离，让连线更短
          itemStyle: {
            borderWidth: 2,
            borderColor: '#fff',
            shadowBlur: 8,
            shadowColor: 'rgba(0, 0, 0, 0.1)',
            shadowOffsetX: 2,
            shadowOffsetY: 2
          }
        }
      ]
    };

    chartInstance.current.setOption(option);

    // 强制展开前3层
    setTimeout(() => {
      if (chartInstance.current) {
        // 展开所有品牌节点
        chartInstance.current.dispatchAction({
          type: 'expand',
          dataIndex: [0, 1, 2] // 展开前3层
        });
      }
    }, 500);

    // 启用缩放和平移
    chartInstance.current.on('dataZoom', function() {
      console.log('图表已缩放');
    });

    // 添加节点点击事件监听
    chartInstance.current.on('click', function(params: any) {
      console.log('点击事件触发:', params); // 调试信息

      // 检查点击的是否是产品节点
      if (params.data && params.data.productCode) {
        // 获取点击的节点数据
        const clickedNode = params.data;

        console.log('检测到产品节点点击:', clickedNode); // 调试信息

        // 构建目标URL - 动态获取当前域名
        const currentOrigin = window.location.origin;
        const targetUrl = `${currentOrigin}/InventoryForecast?productCode=${encodeURIComponent(clickedNode.productCode)}`;

        // 在新标签页中打开
        window.open(targetUrl, '_blank');

        console.log(`跳转到库存预测页面，产品: ${clickedNode.productName || clickedNode.name}, 产品编码: ${clickedNode.productCode}`);
      } else if (params.data && params.data.nodeType === 'mainSpu') {
        // 处理主SPU节点点击
        const mainSpuName = params.data.mainSpuName || params.data.name;
        const brandName = params.data.brandName;
        const specName = params.data.specName;

        console.log('检测到主SPU节点点击:', {
          mainSpuName,
          brandName,
          specName,
          params: params.data
        }); // 调试信息

        // 在脑图数据中查找对应的主SPU节点
        const mainSpuNode = findMainSpuNode(mindMapData, brandName, specName, mainSpuName);
        if (mainSpuNode) {
          console.log('找到主SPU节点，显示旭日图:', mainSpuNode);
          showSunburstChart(mainSpuNode);
        } else {
          console.warn('未找到对应的主SPU节点');
        }
      } else {
        console.log('点击的不是产品节点或主SPU节点:', params.data);
      }
    });

    // 监听窗口大小变化
    const handleResize = () => {
      if (chartInstance.current) {
        chartInstance.current.resize();
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  };

  // 品牌选择处理函数
  const handleBrandChange = (brandCode: string) => {
    console.log('品牌选择变化:', brandCode);
    setSelectedBrandCode(brandCode);
  };

  // 处理产品节点点击事件
  const handleProductClick = (product: OnSaleProduct) => {
    const productCode = product.dim_goods_no;
    if (productCode) {
      // 构建目标URL - 动态获取当前域名
      const currentOrigin = window.location.origin;
      const targetUrl = `${currentOrigin}/InventoryForecast?productCode=${encodeURIComponent(productCode)}`;

      // 在新标签页中打开
      window.open(targetUrl, '_blank');

      console.log(`跳转到库存预测页面，产品编码: ${productCode}`);
    } else {
      console.warn('产品编码为空，无法跳转');
    }
  };

  // 查找主SPU节点的辅助函数
  const findMainSpuNode = (mindMapData: MindMapNode[], brandName?: string, specName?: string, mainSpuName?: string): MindMapNode | null => {
    if (!mainSpuName) return null;

    for (const brand of mindMapData) {
      if (brandName && brand.name !== brandName) continue;

      for (const spec of brand.children || []) {
        if (specName && spec.name !== specName) continue;

        for (const mainSpu of spec.children || []) {
          if (mainSpu.name === mainSpuName || mainSpu.name.replace(/\s*\(\d[\d,]*\)$/, '') === mainSpuName) {
            return mainSpu;
          }
        }
      }
    }

    return null;
  };

  // 创建旭日图数据
  const createSunburstData = (mainSpuNode: MindMapNode): EChartTreeNode => {
    const sunburstNode: EChartTreeNode = {
      name: mainSpuNode.name,
      value: mainSpuNode.salesCount || 0,
      nodeType: 'mainSpu',
      itemStyle: { color: '#F59E0B' },
      label: {
        show: true,
        fontSize: 16,
        fontWeight: 'bold'
      }
    };

    if (mainSpuNode.children && mainSpuNode.children.length > 0) {
      sunburstNode.children = mainSpuNode.children.map(spuNode => {
        const spuChartNode: EChartTreeNode = {
          name: spuNode.name,
          value: spuNode.salesCount || 0,
          nodeType: 'spu',
          itemStyle: { color: '#8B5CF6' },
          label: {
            show: true,
            fontSize: 14,
            fontWeight: '600'
          }
        };

        if (spuNode.children && spuNode.children.length > 0) {
          spuChartNode.children = spuNode.children.map(productNode => {
            const productChartNode: EChartTreeNode = {
              name: productNode.name,
              value: productNode.salesCount || 0,
              nodeType: 'product',
              itemStyle: { color: '#6B7280' },
              label: {
                show: true,
                fontSize: 12,
                fontWeight: 'normal'
              }
            };

            // 为产品节点添加数据
            if (productNode.data) {
              productChartNode.productCode = productNode.data.dim_goods_no;
              productChartNode.productName = productNode.data.产品名称;
              productChartNode.cursor = 'pointer';
            }

            return productChartNode;
          });
        }

        return spuChartNode;
      });
    }

    return sunburstNode;
  };

  // 显示旭日图
  const showSunburstChart = (mainSpuNode: MindMapNode) => {
    setSelectedMainSpu(mainSpuNode);
    const sunburstData = createSunburstData(mainSpuNode);
    setSunburstData(sunburstData);
    setShowSunburst(true);
  };

  // 关闭旭日图
  const closeSunburstChart = () => {
    setShowSunburst(false);
    setSelectedMainSpu(null);
    setSunburstData(null);
    if (sunburstChartInstance.current) {
      sunburstChartInstance.current.dispose();
      sunburstChartInstance.current = null;
    }
  };

  // 创建旭日图
  const createSunburstChart = () => {
    if (!sunburstChartRef.current || !sunburstData) return;

    // 如果图表实例已存在，先销毁
    if (sunburstChartInstance.current) {
      sunburstChartInstance.current.dispose();
    }

    // 创建新的图表实例
    sunburstChartInstance.current = echarts.init(sunburstChartRef.current);

    const option = {
      title: {
        text: `${selectedMainSpu?.name} - SPU和产品层级`,
        left: 'center',
        top: 20,
        textStyle: {
          fontSize: 18,
          fontWeight: 'bold',
          color: '#333'
        }
      },
      tooltip: {
        trigger: 'item',
        formatter: function(params: any) {
          const fullName = params.name;
          const value = params.value || 0;
          const levelNames = ['主SPU', 'SPU', '产品'];
          const level = params.treePathInfo ? params.treePathInfo.length - 1 : 1;
          const levelName = levelNames[level] || '未知';

          // 从名称中提取纯名称（去掉括号和数量）
          const pureName = fullName.replace(/\s*\(\d[\d,]*\)$/, '');

          // 如果是产品节点，添加点击提示
          const clickHint = level === 2 ? '<div style="color: #10B981; font-size: 11px; margin-top: 6px; font-style: italic;">👆 点击查看库存预测</div>' : '';

          return `<div style="padding: 8px;">
            <div style="font-weight: bold; margin-bottom: 4px;">${pureName}</div>
            <div style="color: #666; font-size: 12px;">${levelName}</div>
            <div style="color: #3B82F6; margin-top: 4px;">销量: ${value.toLocaleString()}</div>
            ${clickHint}
          </div>`;
        }
      },
      series: [
        {
          type: 'sunburst',
          data: [sunburstData],
          radius: [0, '90%'],
          label: {
            rotate: 'radial',
            minAngle: 15
          },
          emphasis: {
            focus: 'ancestor'
          },
          levels: [
            {},
            {
              r0: '0%',
              r: '30%',
              label: {
                rotate: 0,
                fontSize: 16,
                fontWeight: 'bold'
              },
              itemStyle: {
                color: '#F59E0B'
              }
            },
            {
              r0: '30%',
              r: '60%',
              label: {
                rotate: 'radial',
                fontSize: 14,
                fontWeight: '600'
              },
              itemStyle: {
                color: '#8B5CF6'
              }
            },
            {
              r0: '60%',
              r: '90%',
              label: {
                rotate: 'radial',
                fontSize: 12,
                fontWeight: 'normal'
              },
              itemStyle: {
                color: '#6B7280'
              }
            }
          ]
        }
      ]
    };

    sunburstChartInstance.current.setOption(option);

    // 添加旭日图点击事件
    sunburstChartInstance.current.on('click', function(params: any) {
      if (params.data && params.data.productCode) {
        // 获取点击的产品节点数据
        const clickedNode = params.data;
        console.log('旭日图中检测到产品节点点击:', clickedNode);

        // 构建目标URL - 动态获取当前域名
        const currentOrigin = window.location.origin;
        const targetUrl = `${currentOrigin}/InventoryForecast?productCode=${encodeURIComponent(clickedNode.productCode)}`;

        // 在新标签页中打开
        window.open(targetUrl, '_blank');

        console.log(`从旭日图跳转到库存预测页面，产品: ${clickedNode.productName || clickedNode.name}, 产品编码: ${clickedNode.productCode}`);
      }
    });

    // 监听窗口大小变化
    const handleResize = () => {
      if (sunburstChartInstance.current) {
        sunburstChartInstance.current.resize();
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  };

  // 切换全屏模式
  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  // 缩放控制
  const zoomIn = () => {
    if (chartInstance.current) {
      const option = chartInstance.current.getOption();
      const currentZoom = option.series?.[0]?.zoom || 1;
      chartInstance.current.setOption({
        series: [{
          zoom: currentZoom * 1.2
        }]
      });
    }
  };

  const zoomOut = () => {
    if (chartInstance.current) {
      const option = chartInstance.current.getOption();
      const currentZoom = option.series?.[0]?.zoom || 1;
      chartInstance.current.setOption({
        series: [{
          zoom: Math.max(currentZoom * 0.8, 0.5)
        }]
      });
    }
  };

  const resetZoom = () => {
    if (chartInstance.current) {
      chartInstance.current.dispatchAction({
        type: 'restore'
      });
    }
  };

  
  // 更新图表和高度
  useEffect(() => {
    if (mindMapData.length > 0) {
      // 重新计算高度（当全屏模式变化时）
      const optimalHeight = calculateOptimalHeight(mindMapData);
      setChartHeight(optimalHeight);

      updateChart();
    }
  }, [mindMapData, isFullscreen]);

  // 监控品牌选择变化
  useEffect(() => {
    console.log('selectedBrandCode 变化:', selectedBrandCode);
    console.log('对应品牌名称:', BRAND_OPTIONS.find(b => b.code === selectedBrandCode)?.name);
  }, [selectedBrandCode]);

  // 初始化和品牌变化时获取数据
  useEffect(() => {
    fetchOnSaleProducts();
  }, [selectedBrandCode]); // 当品牌选择变化时重新获取数据

  // 旭日图显示时创建图表
  useEffect(() => {
    if (showSunburst && sunburstData) {
      setTimeout(() => {
        createSunburstChart();
      }, 100);
    }
  }, [showSunburst, sunburstData]);

  // 清理图表实例
  useEffect(() => {
    return () => {
      if (chartInstance.current) {
        chartInstance.current.dispose();
      }
      if (sunburstChartInstance.current) {
        sunburstChartInstance.current.dispose();
      }
    };
  }, []);

  return (
    <div className={cn(
      "min-h-screen bg-gray-50",
      isFullscreen ? "fixed inset-0 z-50 bg-white" : ""
    )}>
      {/* 页面标题区域 */}
      {!isFullscreen && (
        <div className="p-6">
          <div className="flex items-center gap-6">
            {/* 页面标题 */}
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-gray-900">在售商品情况</h1>
              <p className="text-gray-600 mt-1">
                在售商品层级关系，支持品牌筛选
              </p>
            </div>

            {/* 品牌选择区域 */}
            <div className="flex-1 max-w-xs">
              <select
                value={selectedBrandCode}
                onChange={(e) => handleBrandChange(e.target.value)}
                disabled={loading}
                className="w-full h-[42px] bg-white border border-gray-300 hover:border-blue-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all duration-200 shadow-sm rounded-md px-3 py-2 text-sm"
              >
                <option value="" disabled>请选择品牌</option>
                {BRAND_OPTIONS.map((brand) => (
                  <option key={brand.code} value={brand.code}>
                    {brand.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      )}

      {/* 脑图展示区域 */}
      <div className={cn(
        "px-6 pb-6",
        isFullscreen ? "p-4 h-screen" : ""
      )}>
        <div className={cn(
          "bg-gradient-to-br from-blue-50 via-white to-purple-50 rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-500 border border-blue-100",
          isFullscreen ? "h-full p-4" : "p-8"
        )}>
          {/* 头部标题和控制按钮 */}
          <div className="flex items-center justify-between mb-6">
            <h3 className={cn(
              "font-bold flex items-center gap-3 text-slate-800",
              isFullscreen ? "text-xl" : "text-2xl"
            )}>
              <Package className={cn("text-blue-600", isFullscreen ? "w-5 h-5" : "w-6 h-6")} />
              在售商品(按规格展示)
            </h3>

            {/* 控制按钮 */}
            <div className="flex items-center gap-2">
              {/* 缩放控制按钮 */}
              <div className="flex items-center bg-white rounded-lg shadow-md border border-gray-200">
                <button
                  onClick={zoomIn}
                  className="p-2 text-gray-700 hover:text-gray-900 hover:bg-gray-50 rounded-l-lg transition-all duration-300"
                  title="放大"
                >
                  <ZoomIn className="w-4 h-4" />
                </button>
                <button
                  onClick={zoomOut}
                  className="p-2 text-gray-700 hover:text-gray-900 hover:bg-gray-50 transition-all duration-300"
                  title="缩小"
                >
                  <ZoomOut className="w-4 h-4" />
                </button>
                <button
                  onClick={resetZoom}
                  className="p-2 text-gray-700 hover:text-gray-900 hover:bg-gray-50 rounded-r-lg transition-all duration-300"
                  title="重置视图"
                >
                  <RotateCcw className="w-4 h-4" />
                </button>
              </div>

              {/* 全屏控制按钮 */}
              <button
                onClick={toggleFullscreen}
                className={cn(
                  "flex items-center gap-2 px-4 py-2 bg-white rounded-lg shadow-md hover:shadow-lg transition-all duration-300 border border-gray-200",
                  "text-gray-700 hover:text-gray-900"
                )}
              >
                {isFullscreen ? (
                  <>
                    <Minimize2 className="w-4 h-4" />
                    退出全屏
                  </>
                ) : (
                  <>
                    <Maximize2 className="w-4 h-4" />
                    全屏显示
                  </>
                )}
              </button>
            </div>
          </div>

          {/* 图表容器 */}
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-12 h-12 animate-spin text-blue-500" />
              <span className="ml-4 text-gray-600 text-lg">数据加载中...</span>
            </div>
          ) : mindMapData.length === 0 ? (
            <div className="text-center py-20 text-gray-500" style={{ height: `${isFullscreen ? window.innerHeight - 120 : 400}px` }}>
              <Package className="w-16 h-16 mx-auto mb-6 text-gray-300" />
              <p className="text-lg">暂无数据</p>
            </div>
          ) : (
            <div
              ref={chartRef}
              className={cn(
                "w-full transition-all duration-300"
              )}
              style={{ height: `${chartHeight}px` }}
            />
          )}
        </div>
      </div>

      {/* 旭日图弹窗 */}
      {showSunburst && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-4xl max-h-[90vh] m-4">
            {/* 弹窗头部 */}
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-xl font-bold text-gray-900">
                {selectedMainSpu?.name} - SPU和产品层级旭日图
              </h2>
              <button
                onClick={closeSunburstChart}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                title="关闭旭日图"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* 旭日图容器 */}
            <div className="p-6">
              <div
                ref={sunburstChartRef}
                className="w-full"
                style={{ height: '600px' }}
              />
            </div>

            {/* 弹窗底部 */}
            <div className="p-6 border-t bg-gray-50 rounded-b-lg">
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-600">
                  点击产品节点可跳转到库存预测页面
                </p>
                <button
                  onClick={closeSunburstChart}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200"
                >
                  关闭
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OnSaleProducts;